import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Pagination,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
} from '@mui/material';
import PageContainer from '../../../components/Common/PageContainer';
import PageTitle from '../../../components/Common/PageTitle';
import useWorkspaceChange from '../../../hooks/useWorkspaceChange';
import { useStoreList } from '../../../context/StoreListContext';
import {
    DEFAULT_ITEMS_PER_PAGE,
    useDeleteCommunicationInput,
    useSearchCommunications,
    CommunicationSearchFilters as Filters,
    SpecificStatus,
} from '../../../hooks/Marketing';
import CommunicationSearchFilters from './CommunicationSearchFilters';
import CommunicationResultCard from './CommunicationResultCard';
import ConfirmDialog from '../../Clients/ListImport/components/ConfirmDialog';

const isMainEntity = (entityType?: string) => entityType === 'COMPANY' || entityType === 'ROOT';

const ITEMS_PER_PAGE_OPTIONS = [4, 8, 12, 16] as const;

const buildDefaultFilters = (defaultSpecificStatuses: SpecificStatus[]): Filters => ({
    allStores: true,
    entityId: null,
    defTitle: '',
    communicationInputId: '',
    creationDateFrom: '',
    creationDateTo: '',
    defChannelIds: [],
    specificStatuses: defaultSpecificStatuses,
    actionStatusIds: [],
});

interface AdvancedSearchProps {
    title?: string;
    defaultSpecificStatuses?: SpecificStatus[];
}

const AdvancedSearch = ({ title = 'Recherche de communications', defaultSpecificStatuses = [] }: AdvancedSearchProps) => {
    const { stores, loading: storesLoading } = useStoreList();
    const { communications, totalItems, isLoading, error, search } = useSearchCommunications();
    const { deleteCommunicationInput } = useDeleteCommunicationInput();

    const DEFAULT_FILTERS = buildDefaultFilters(defaultSpecificStatuses);
    const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
    const [page, setPage] = useState(1);
    const [pageInput, setPageInput] = useState('1');
    const [itemsPerPage, setItemsPerPage] = useState<number | 'all'>(DEFAULT_ITEMS_PER_PAGE);
    const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

    useEffect(() => {
        if (!stores || filters.entityId) return;
        const defaultStore = stores.find((store) => isMainEntity(store.ENTITY_TYPE)) ?? stores[0];
        if (defaultStore) {
            setFilters((prev) => ({ ...prev, entityId: defaultStore.ENTITY_ID }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stores]);

    const runSearch = useCallback(
        (targetPage: number, targetItemsPerPage: number | 'all' = itemsPerPage) => {
            setPage(targetPage);
            setPageInput(String(targetPage));
            search(filters, targetPage, targetItemsPerPage);
        },
        [filters, search, itemsPerPage]
    );

    useEffect(() => {
        runSearch(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useWorkspaceChange(() => {
        const defaultStore = stores?.find((store) => isMainEntity(store.ENTITY_TYPE)) ?? stores?.[0];
        setFilters({ ...DEFAULT_FILTERS, entityId: defaultStore?.ENTITY_ID ?? null });
        setPage(1);
        setPageInput('1');
        search({ ...DEFAULT_FILTERS, entityId: defaultStore?.ENTITY_ID ?? null }, 1, itemsPerPage);
    });

    const handleReset = () => {
        const defaultStore = stores?.find((store) => isMainEntity(store.ENTITY_TYPE)) ?? stores?.[0];
        setFilters({ ...DEFAULT_FILTERS, entityId: defaultStore?.ENTITY_ID ?? null });
    };

    const handleItemsPerPageChange = (event: SelectChangeEvent<string>) => {
        const value = event.target.value === 'all' ? 'all' : Number(event.target.value);
        setItemsPerPage(value);
        runSearch(1, value);
    };

    const handleDeleteConfirm = async () => {
        if (pendingDeleteId == null) return;
        const ok = await deleteCommunicationInput(pendingDeleteId);
        setPendingDeleteId(null);
        if (ok) runSearch(page);
    };

    const pageCount = itemsPerPage === 'all' ? 1 : Math.max(1, Math.ceil(totalItems / itemsPerPage));
    const rangeStart = totalItems === 0 ? 0 : itemsPerPage === 'all' ? 1 : (page - 1) * itemsPerPage + 1;
    const rangeEnd = itemsPerPage === 'all' ? totalItems : Math.min(page * itemsPerPage, totalItems);

    const handlePageInputSubmit = () => {
        const target = Number(pageInput);
        if (!Number.isInteger(target) || target < 1 || target > pageCount) {
            setPageInput(String(page));
            return;
        }
        runSearch(target);
    };

    return (
        <PageContainer>
            <PageTitle>{title}</PageTitle>

            <CommunicationSearchFilters
                stores={stores}
                storesLoading={storesLoading}
                filters={filters}
                onChange={setFilters}
                onSearch={() => runSearch(1)}
                onReset={handleReset}
            />

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    {isLoading ? 'Recherche en cours…' : `Résultats ${rangeStart} à ${rangeEnd} sur un total de ${totalItems}`}
                </Typography>

                <FormControl size="small" sx={{ minWidth: 170 }}>
                    <InputLabel id="items-per-page-label">Résultats par page</InputLabel>
                    <Select
                        labelId="items-per-page-label"
                        label="Résultats par page"
                        value={itemsPerPage === 'all' ? 'all' : String(itemsPerPage)}
                        onChange={handleItemsPerPageChange}
                    >
                        {ITEMS_PER_PAGE_OPTIONS.map((option) => (
                            <MenuItem key={option} value={String(option)}>{option}</MenuItem>
                        ))}
                        <MenuItem value="all">Tous</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Grid container spacing={2}>
                    {communications.map((communication) => (
                        <Grid size={{ xs: 12, sm: 6, md: 3 }} key={communication.id}>
                            <CommunicationResultCard data={communication} onDelete={setPendingDeleteId} />
                        </Grid>
                    ))}
                </Grid>
            )}

            {!isLoading && itemsPerPage !== 'all' && pageCount > 1 && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mt: 3, flexWrap: 'wrap' }}>
                    <Pagination count={pageCount} page={page} onChange={(_e, value) => runSearch(value)} color="primary" />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                            size="small"
                            type="number"
                            label="Page"
                            value={pageInput}
                            onChange={(e) => setPageInput(e.target.value)}
                            onBlur={handlePageInputSubmit}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handlePageInputSubmit();
                            }}
                            inputProps={{ min: 1, max: pageCount }}
                            sx={{ width: 90 }}
                        />
                        <Typography variant="body2" color="text.secondary">/ {pageCount}</Typography>
                    </Box>
                </Box>
            )}

            <ConfirmDialog
                open={pendingDeleteId != null}
                title="Supprimer la communication"
                message="Voulez-vous vraiment supprimer cette communication ? Cette action est irréversible."
                confirmText="Supprimer"
                confirmColor="error"
                onConfirm={handleDeleteConfirm}
                onCancel={() => setPendingDeleteId(null)}
            />
        </PageContainer>
    );
};

export default AdvancedSearch;
